import React, { useState, useEffect, useRef, createRef } from "react";
import { showReportAllOperationsNotification } from "../../materialCommitment/helpers/Notifications";
import { getMinReportingDate, getMaxReportingDate } from "./helpers/Dates";
import moment from "moment";
import ReportingHeader from "./components/ReportingHeader";
import DatePickingContainer from "./components/DatePickingContainer";
import BottomActionContainer from "./components/BottomActionContainer";
import OperationReport from "./components/OperationReport";
import { isMaterialOrGD } from "../../materialCommitment/helpers/Categories";
import translate from "../../reusable/translate";
import scrollIntoView from "scroll-into-view-if-needed";

const ReportingContainer = ({ commitment }) => {
  const [useDraft, setUseDraft] = useState([]);
  const minReportingDate = moment(getMinReportingDate(commitment));
  const maxReportingDate = moment(getMaxReportingDate(commitment));
  const gdReportingDisabled =
    commitment.isGreenDeal &&
    moment().isAfter(
      moment()
        .startOf("year")
        .add(2, "months")
    );

  const [startingDate, setStartingDate] = useState(minReportingDate);
  const [endingDate, setEndingDate] = useState(maxReportingDate);

  const [sendAllReports, setSendAllReports] = useState("");
  const [reportsReady, setReportsReady] = useState([]);
  const [unfinishedReports, setUnfinishedReports] = useState([]);

  const [agreeTerms, setAgreeTerms] = useState(false);

  const operationRefs = useRef([]); // References to operations

  // Initialize operation refs
  if(operationRefs.current.length !== commitment.operations.length){
    operationRefs.current = Array(commitment.operations.length)
      .fill()
      .map((_,i) => operationRefs.current[i] || createRef())
  }

  const updateReportsReady = (operation, ready) => {
    if (!reportsReady.some(report => report.id === operation.id) && ready) {
      setReportsReady(prev => [...prev, operation]);
    } else if (reportsReady.some(report => report.id === operation.id) && ready) {
      setReportsReady(
        prevstate => prevstate.map(report => (report.id === operation.id) ? operation : report)
      );
    } else if (reportsReady.some(report => report.id === operation.id) && !ready) {
      setReportsReady(
        reportsReady.filter(report => report.id !== operation.id)
      );
    }
  };

  const highlightUnfinishedReports = () => {
    // Find unfinished reports
    const unfinishedReports = commitment.operations.filter((operation) => {
      const isMatched = reportsReady.some(
        (report) => operation.operationId === report.id
      );

      return !isMatched;
    });

    // Set unfinished reports. This triggers validateReport through a useEffect 
    // in OperationReport which highlights the fields that have missing inputs
    setUnfinishedReports(unfinishedReports)
    
    // Focus and scroll to the first operation report that is missing inputs
    commitment.operations.forEach((operation, index) => {
      unfinishedReports.forEach((report) => {
        if (operation.operationId == report.operationId) {

          operationRefs.current[index].current.focus()
          scrollIntoView( operationRefs.current[index].current, {
            scrollMode: "if-needed",
            block: "center",
            inline: "nearest",
            behavior: "smooth"
          })
        }
        return
      });

      return 
    });


  };

  /**
   * Function for triggering report saving in green deals or material commitments via BottomActionContainer
   * @param state either "approve" or "draft" - indicates if the report is saved as published or draft
   * @returns boolean - true indicates that validations passed -> saving started, either false is returned
   */
  const checkAndSendAllReports = state => {
    //Save as draft -> no need to do validations
    if (state === "draft") {
      setSendAllReports("draft");
      return true;
    }
    if (reportsReady.length === commitment.operations.length) {
      setSendAllReports("approved");
      return true;
    } else {
      setSendAllReports("");
      showReportAllOperationsNotification();
      highlightUnfinishedReports();
      return false;
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (
    isMaterialOrGD(commitment.commitmentType) &&
    moment()
      .startOf("year")
      .isBefore(commitment.startDate)
  ) {
    return (
      <div className="container">
        <ReportingHeader commitment={commitment} />
        <div>
          <h3>
            Sitoumusta ei voi vielä raportoida. Sitoumus raportoidaan
            ensimmäisen kerran alkamispäivämäärää seuraavan vuoden alussa.
          </h3>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      {/* gdReportingDisabled ?
                <h3>HUOM! Sitoumusta ei voi enää raportoida. Sitoumus tulee raportoida 6 kk aikana vuodenvaihteesta. Ole hyvä ja ota yhteyttä Green dealin yhteyshenkilöön raportoidaksesi sitoumuksen</h3>
                : ""
            */}

      <ReportingHeader commitment={commitment} />

      {/* "Toimenpiteet" header*/}
      <h3>{translate({ textKey: "sit.commitment.operations" })}</h3>

      <DatePickingContainer
        commitment={commitment}
        startingDate={startingDate}
        endingDate={endingDate}
        setters={{ setStartingDate, setEndingDate }}
        minReportingDate={minReportingDate}
        maxReportingDate={maxReportingDate}
        disabled={
          isMaterialOrGD(commitment.commitmentType) ||
          commitment.commitmentType === "CIRCULAR_ECONOMY"
        }
      />

      {startingDate &&
        endingDate &&
        commitment.operations.map((mappedOperation, index) => (
          <OperationReport
            key={mappedOperation.operationId}
            operation={mappedOperation}
            startingDate={startingDate}
            endingDate={endingDate}
            commitment={commitment}
            sendReport={sendAllReports}
            reportsReady={reportsReady}
            setReportsReady={updateReportsReady}
            operationRef={operationRefs.current[index]}
            unfinishedReports={unfinishedReports}
            useDraft={useDraft}
            setUseDraft={setUseDraft}
          />
        ))}

      <BottomActionContainer
        commitment={commitment}
        startingDate={startingDate}
        endingDate={endingDate}
        checkAndSendAllReports={checkAndSendAllReports}
        agreeTerms={agreeTerms}
        setAgreeTerms={setAgreeTerms}
        reportsReady={reportsReady}
      />
    </div>
  );
};

export default ReportingContainer;



// WEBPACK FOOTER //
// ./src/components/profile/reports/ReportingContainer.jsx